import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Loader2, ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, fileUrl, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LABEL = { awaiting_approval: "Awaiting approval", in_production: "In production", shipped: "Shipped",
  fulfillment_error: "Needs attention", canceled: "Canceled" };

const OrderRow = ({ o, onAction, busy }) => (
  <div className="flex flex-wrap items-center gap-4 rounded-2xl bg-white border border-[#EAE3D9] p-4" data-testid={`admin-order-${o.order_id}`}>
    <img src={fileUrl(o.image_path)} alt="" className="h-16 w-16 rounded-xl object-cover" />
    <div className="flex-1 min-w-[12rem]">
      <p className="font-bold">{o.product_name}{o.size ? ` · ${o.size}` : ""}{o.color ? ` · ${o.color}` : ""}</p>
      <p className="text-xs text-[#6E687A]">{o.shipping?.full_name} · {o.shipping?.city}, {o.shipping?.country} · <span className="font-mono">{o.order_id}</span></p>
      {o.fulfillment?.error && <p className="text-xs text-red-600 mt-1">{o.fulfillment.error}</p>}
    </div>
    <span className="font-mono font-bold">${o.price.toFixed(2)}</span>
    <span className="rounded-full bg-[#F3EFFF] text-[#8A4FFF] px-3 py-1 text-xs font-bold" data-testid={`admin-order-status-${o.order_id}`}>
      {LABEL[o.status] || o.status}{o.status === "canceled" && o.refunded ? " · refunded" : ""}
    </span>
    {o.status === "awaiting_approval" && (
      <div className="flex gap-2">
        <button disabled={busy} onClick={() => onAction(o, "approve")} data-testid={`admin-approve-${o.order_id}`}
          className="flex items-center gap-1 rounded-full bg-[#36D399] text-[#1E1B26] px-4 py-2 text-sm font-bold sticker sticker-press disabled:opacity-60"><Check className="h-4 w-4" /> Approve & print</button>
        <button disabled={busy} onClick={() => onAction(o, "cancel")} data-testid={`admin-cancel-${o.order_id}`}
          className="flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-bold border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-60"><X className="h-4 w-4" /> Cancel & refund</button>
      </div>
    )}
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const [hold, setHold] = useState(null);
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/orders").then((r) => setOrders(r.data)), []);
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/settings").then((r) => setHold(r.data.hold_orders));
    load();
  }, [user, load]);

  if (user && user.role !== "admin") return <Navigate to="/studio" replace />;

  const toggle = async (v) => {
    setHold(v);
    try {
      await api.put("/admin/settings", { hold_orders: v });
      toast.success(v ? "New orders will wait for your approval" : "New orders go straight to print");
    } catch (e) { setHold(!v); toast.error(formatError(e)); }
  };

  const act = async (o, action) => {
    if (action === "cancel" && !window.confirm("Cancel this order and refund the customer?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.order_id}/${action}`);
      toast.success(action === "approve" ? "Sent to production" : "Order canceled");
      await load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  const pending = (orders || []).filter((o) => o.status === "awaiting_approval");
  const rest = (orders || []).filter((o) => o.status !== "awaiting_approval");

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-page">
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Order Review</h1>
      <div className="mt-8 flex flex-wrap items-center gap-4 rounded-3xl bg-[#1E1B26] text-white p-6">
        <ShieldCheck className="h-8 w-8 text-[#FFC72C]" />
        <div className="flex-1 min-w-[14rem]">
          <p className="font-display text-xl font-semibold">Hold new orders for my approval</p>
          <p className="text-sm text-white/70">When on, paid orders are created in Printify but won't print until you approve them here.</p>
        </div>
        {hold === null ? <Loader2 className="h-5 w-5 animate-spin" /> :
          <Switch checked={hold} onCheckedChange={toggle} data-testid="admin-hold-toggle" className="data-[state=checked]:bg-[#FF5A79]" />}
      </div>

      <h2 className="mt-12 text-xl font-semibold">Awaiting approval <span className="text-[#6E687A]">({pending.length})</span></h2>
      <div className="mt-4 space-y-3" data-testid="admin-pending-list">
        {orders && pending.length === 0 && <p className="text-[#6E687A]">Nothing waiting. Nice and tidy.</p>}
        {pending.map((o) => <OrderRow key={o.order_id} o={o} onAction={act} busy={busy} />)}
      </div>

      <h2 className="mt-12 text-xl font-semibold">All recent orders</h2>
      <div className="mt-4 space-y-3" data-testid="admin-all-list">
        {orders === null && <div className="shimmer h-20 rounded-2xl" />}
        {orders && rest.length === 0 && <p className="text-[#6E687A]">No paid orders yet.</p>}
        {rest.map((o) => <OrderRow key={o.order_id} o={o} onAction={act} busy={busy} />)}
      </div>
    </main>
  );
}
